import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useWishlist } from "@/hooks/useWishlist";
import { formatPrice } from "@/data/products";
import { getOptimizedImageUrl } from "@/lib/image-optimization";

interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  original_price?: number | null;
  images: string[];
  is_new?: boolean;
  is_featured?: boolean;
  category?: { name: string; slug: string } | null;
  category_id?: string | null;
  description?: string | null;
  rating?: number | null;
  review_count?: number | null;
  in_stock?: boolean | null;
  colors?: string[] | null;
  materials?: string[] | null;
}

interface ProductCardProps {
  product: Product;
}

export function ProductCard({ product }: ProductCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const inWishlist = isInWishlist(product.id);
  const categoryName = product.category?.name || "";
  
  const handleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (inWishlist) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product as any);
    }
  };

  const discountPercentage = product.original_price 
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  return (
    <>
      <Link to={`/product/${product.slug}`} className="group block">
        {/* Image Container - 3:4 Aspect Ratio */}
        <div className="relative overflow-hidden bg-ivory-dark aspect-[2/3] mb-5">
          {/* Shimmer skeleton loader with Spinner */}
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_ease-in-out_infinite] flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-charcoal/20 border-t-charcoal rounded-full animate-spin" />
            </div>
          )}
          
          {/* Main Image */}
          <img
            src={getOptimizedImageUrl(product.images?.[0])}
            alt={product.name}
            width={400}
            height={600}
            decoding="async"
            className={`w-full h-full object-contain transition-all duration-700 group-hover:scale-105 ${
              imageLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              e.currentTarget.src = "/placeholder.svg";
              setImageLoaded(true);
            }}
            loading="lazy"
          />
          
          {/* Hover Image (if available) */}
          {product.images?.[1] && (
            <img
              src={getOptimizedImageUrl(product.images[1])}
              alt={product.name}
              width={400}
              height={600}
              decoding="async"
              className="absolute inset-0 w-full h-full object-contain opacity-0 transition-opacity duration-700 group-hover:opacity-100"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              loading="lazy"
            />
          )}
          
          {/* Badges - Top Left */}
          <div className="absolute top-0 left-0 flex flex-col">
            {product.is_new && (
              <span className="bg-charcoal text-white text-[10px] uppercase tracking-[0.15em] px-4 py-2 font-semibold">
                New
              </span>
            )}
            {discountPercentage > 0 && (
              <span className="bg-accent text-accent-foreground text-[10px] uppercase tracking-[0.15em] px-4 py-2 font-semibold">
                -{discountPercentage}%
              </span>
            )}
          </div>

          {/* Wishlist Button - Top Right */}
          <button 
            onClick={handleWishlist}
            className={`absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${
              inWishlist 
                ? "bg-accent text-white" 
                : "bg-white/80 text-charcoal opacity-0 group-hover:opacity-100"
            } hover:scale-110`}
            aria-label={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
          >
            <Heart size={18} className={inWishlist ? "fill-current" : ""} />
          </button>

        </div>

        {/* Product Info */}
        <div className="text-center px-2">
          {/* Category */}
          {categoryName && (
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
              {categoryName}
            </p>
          )}
          
          {/* Product Name */}
          <h3 className="font-accent text-lg md:text-xl mb-3 group-hover:text-primary transition-colors duration-300 leading-tight">
            {product.name}
          </h3>
          
          {/* Price */}
          <div className="flex items-center justify-center gap-3">
            <span className="text-base font-semibold tracking-wide">
              {formatPrice(product.price)}
            </span>
            {product.original_price && (
              <span className="text-muted-foreground line-through text-sm">
                {formatPrice(product.original_price)}
              </span>
            )}
          </div>
        </div>
      </Link>

    </>
  );
}